// The letter-reorder POST, extracted from compose.ts so the ordering and
// error handling are unit-testable without a DOM (compose.test.ts). The DOM
// reading/writing stays in compose.ts and is injected here as plain data and
// callbacks.

export interface ReorderIo {
  /** GET /groups/{id}/letter — a fresh mtproto_id → dm_message_id map; handles
   *  messages added live this session whose dmId we don't have locally. */
  fetchLetter: () => Promise<{
    items?: { dm_message_id: number; mtproto_id: number | null }[] | null;
  }>;
  /** POST /groups/{id}/letter/reorder with the full order. */
  postOrder: (dmMessageIds: number[]) => Promise<unknown>;
  /** A visible block couldn't be mapped to a dm_message_id. */
  onUnresolved: () => void;
  /** The fetch or the POST failed — the server does NOT have the new order. */
  onFailed: () => void;
  /** The server accepted the new order. */
  onSaved: () => void;
}

/** Re-POST the current on-screen order of blocks to the reorder API. The
 *  server's set_letter_order makes the letter EXACTLY the posted ids in order,
 *  so the caller passes every visible block; any letter item not represented
 *  (e.g. a DM the user deleted in Telegram) drops out — correct self-healing,
 *  same semantics as the bot's /arrange.
 *
 *  `childMtpIds` is the on-screen block order as MTProto ids; `blockDmId` is
 *  the local fallback map (an item resolved this session carries its own
 *  dm_message_id even before it's frozen server-side). */
export async function postLetterReorder(
  childMtpIds: number[],
  blockDmId: (mtpId: number) => number | null,
  io: ReorderIo
): Promise<void> {
  try {
    const fresh = await io.fetchLetter();
    const mtpToDm = new Map<number, number>();
    for (const item of fresh.items || []) {
      if (item.mtproto_id != null) mtpToDm.set(item.mtproto_id, item.dm_message_id);
    }
    const ordered: number[] = [];
    for (const mtId of childMtpIds) {
      const dmId = mtpToDm.get(mtId) ?? blockDmId(mtId);
      if (dmId == null) {
        // A visible block we can't resolve to a dm_message_id. Posting the
        // partial list would tell the server to DELETE this item
        // (set_letter_order keeps exactly what's sent) — i.e. reordering would
        // lose a message. Abort rather than drop content; the DOM order is
        // harmless until the ids resolve on the next load.
        io.onUnresolved();
        return;
      }
      ordered.push(dmId);
    }
    if (!ordered.length) return;
    await io.postOrder(ordered);
  } catch {
    // SortableJS already mutated the DOM, so the user is looking at an order
    // the server never got — surface it, never swallow it (a reload would
    // silently revert their reorder).
    io.onFailed();
    return;
  }
  io.onSaved();
}
